"use client";

import { c as _c } from "react/compiler-runtime";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { FaDownload } from "react-icons/fa6";
import { FiClock, FiRotateCcw } from "react-icons/fi";
import dynamic from "next/dynamic";
import DeleteConfirmationModal from "@/components/delete-confirmation-modal";
import ProviderAvatar from "@/components/provider-avatar";
import { Button } from "@/components/ui";
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { formatDateTime, formatStaffName } from "@/lib/format-data";
import { toast } from "@/lib/use-toast";
import { Annotation } from "@/types/annotation";
import { NoteAttachment } from "@/types/notes";
import { noteQueryKeys } from "@/api/provider/scribe/note-query";
import { getAttachmentAnnotationHistory, restoreAttachmentAnnotations } from "@/api/provider/scribe/notes";

// Dynamically import ImageAnnotator with SSR disabled
const ImageAnnotator = dynamic(() => import("@/app/[tenant]/(console)/scribe/playground/[id]/image-annotator"), {
  ssr: false,
  loading: () => <div className="flex h-48 items-center justify-center">
        <div className="text-muted-foreground">Loading...</div>
      </div>
});
interface AnnotationHistoryModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  attachmentId: string;
  onRestoreComplete?: () => void;
  onRestore?: (historyId: string) => Promise<void>;
  isRestoring?: boolean;
  currentAnnotations?: Annotation[];
}
export default function AnnotationHistoryModal(t0) {
  const $ = _c(47);
  const {
    open,
    onOpenChange,
    attachmentId,
    onRestoreComplete,
    onRestore,
    isRestoring: t1,
    currentAnnotations
  } = t0;
  const isRestoring = t1 === undefined ? false : t1;
  const [selectedHistoryId, setSelectedHistoryId] = useState(null);
  const [showRestoreConfirm, setShowRestoreConfirm] = useState(false);
  const queryClient = useQueryClient();
  let t2;
  if ($[0] !== attachmentId) {
    t2 = noteQueryKeys.attachmentAnnotationHistory(attachmentId);
    $[0] = attachmentId;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== attachmentId) {
    t3 = () => getAttachmentAnnotationHistory(attachmentId);
    $[2] = attachmentId;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = open && !!attachmentId;
  let t5;
  if ($[4] !== t2 || $[5] !== t3 || $[6] !== t4) {
    t5 = {
      queryKey: t2,
      queryFn: t3,
      enabled: t4,
      select: _temp2
    };
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const {
    data: history,
    isLoading
  } = useQuery(t5);
  let t6;
  if ($[8] !== attachmentId) {
    t6 = async historyId => await restoreAttachmentAnnotations(attachmentId, historyId);
    $[8] = attachmentId;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== attachmentId || $[11] !== onOpenChange || $[12] !== onRestoreComplete || $[13] !== queryClient) {
    t7 = async () => {
      await queryClient.invalidateQueries({
        queryKey: noteQueryKeys.attachmentAnnotationHistory(attachmentId)
      });
      await queryClient.refetchQueries({
        predicate: _temp3
      });
      toast({
        title: "Success",
        description: "Annotations restored successfully",
        variant: "success"
      });
      setShowRestoreConfirm(false);
      setSelectedHistoryId(null);
      onOpenChange(false);
      onRestoreComplete?.();
    };
    $[10] = attachmentId;
    $[11] = onOpenChange;
    $[12] = onRestoreComplete;
    $[13] = queryClient;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t6 || $[16] !== t7) {
    t8 = {
      mutationFn: t6,
      onSuccess: t7,
      onError: _temp4
    };
    $[15] = t6;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const restoreMutation = useMutation(t8);
  const restoreIsPending = restoreMutation.isPending || isRestoring;
  let t9;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = historyId_0 => {
      setSelectedHistoryId(historyId_0);
      setShowRestoreConfirm(true);
    };
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const handleRestoreClick = t9;
  let t10;
  if ($[19] !== onOpenChange || $[20] !== onRestore || $[21] !== onRestoreComplete || $[22] !== restoreMutation || $[23] !== selectedHistoryId) {
    t10 = async () => {
      if (!selectedHistoryId) {
        return;
      }
      if (onRestore) {
        await onRestore(selectedHistoryId);
        setShowRestoreConfirm(false);
        setSelectedHistoryId(null);
        onOpenChange(false);
        onRestoreComplete?.();
        return;
      }
      await restoreMutation.mutateAsync(selectedHistoryId);
    };
    $[19] = onOpenChange;
    $[20] = onRestore;
    $[21] = onRestoreComplete;
    $[22] = restoreMutation;
    $[23] = selectedHistoryId;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  const handleConfirmRestore = t10;
  const handleDownload = _temp5;
  let t11;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <DialogHeader><DialogTitle>Annotation History</DialogTitle></DialogHeader>;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== currentAnnotations || $[27] !== history || $[28] !== isLoading || $[29] !== restoreIsPending) {
    t12 = <DialogBody className="max-h-[70vh] overflow-y-auto">{isLoading ? <div className="flex items-center justify-center py-8"><div className="text-muted-foreground">Loading history...</div></div> : !history || history.length === 0 ? <div className="flex items-center justify-center py-8"><div className="text-muted-foreground">No annotation history found</div></div> : <div className="flex flex-col gap-4">{history.map(entry_0 => {
          const providerName = entry_0.provider ? formatStaffName(entry_0.provider) : "Unknown Provider";
          const providerId = entry_0.provider?.id || "";
          const isCurrentVersion = history.indexOf(entry_0) === 0;
          return <div key={entry_0.id} className="flex flex-col gap-3 rounded-lg border p-4"><div className="relative h-64 w-full overflow-hidden rounded border bg-muted">{entry_0.signedUrl && <ImageAnnotator imageUrl={entry_0.signedUrl} annotations={isCurrentVersion && currentAnnotations ? currentAnnotations : entry_0.annotations || []} readOnly={true} hideDownload={true} className="h-full w-full" onAnnotationsChange={_temp6} />}</div><div className="flex items-center gap-2">{providerId && <ProviderAvatar providerId={providerId} size={24} variant="secondary" />}<div className="flex-1"><div className="text-body-strong text-foreground">{providerName}</div><div className="flex items-center gap-1 text-caption text-muted-foreground"><FiClock className="h-3 w-3" />{entry_0.createdDate ? <span>{formatDateTime(new Date(entry_0.createdDate))}</span> : <span>{"Unknown date"}</span>}</div></div>{entry_0.signedUrl && <Button variant="ghost" size="sm" onClick={() => handleDownload(entry_0)} icon={<FaDownload className="h-4 w-4" />} className="flex-shrink-0" title="Download image" />}</div><Button variant="secondary" size="sm" onClick={() => handleRestoreClick(entry_0.id)} disabled={restoreIsPending || isCurrentVersion} icon={isCurrentVersion ? undefined : <FiRotateCcw className="h-4 w-4" />} className="w-full">{isCurrentVersion ? "Current Version" : "Restore This Version"}</Button></div>;
        })}</div>}</DialogBody>;
    $[26] = currentAnnotations;
    $[27] = history;
    $[28] = isLoading;
    $[29] = restoreIsPending;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[31] !== onOpenChange) {
    t13 = <DialogFooter><Button variant="secondary" onClick={() => onOpenChange(false)}>Close</Button></DialogFooter>;
    $[31] = onOpenChange;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] !== t12 || $[34] !== t13) {
    t14 = <DialogContent className="max-h-[90vh] max-w-2xl">{t11}{t12}{t13}</DialogContent>;
    $[33] = t12;
    $[34] = t13;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] !== onOpenChange || $[37] !== open || $[38] !== t14) {
    t15 = <Dialog open={open} onOpenChange={onOpenChange}>{t14}</Dialog>;
    $[36] = onOpenChange;
    $[37] = open;
    $[38] = t14;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  let t16;
  if ($[40] !== handleConfirmRestore || $[41] !== restoreIsPending || $[42] !== showRestoreConfirm) {
    t16 = <DeleteConfirmationModal isOpen={showRestoreConfirm} setIsOpen={setShowRestoreConfirm} onConfirm={handleConfirmRestore} title="Restore to This Version?" subtitle="This will restore the annotations from this history entry and permanently delete all revisions created after this point. This action cannot be undone." confirmText="Restore" isLoading={restoreIsPending} />;
    $[40] = handleConfirmRestore;
    $[41] = restoreIsPending;
    $[42] = showRestoreConfirm;
    $[43] = t16;
  } else {
    t16 = $[43];
  }
  let t17;
  if ($[44] !== t15 || $[45] !== t16) {
    t17 = <>{t15}{t16}</>;
    $[44] = t15;
    $[45] = t16;
    $[46] = t17;
  } else {
    t17 = $[46];
  }
  return t17;
}
function _temp6() {}
function _temp5(entry) {
  if (!entry.signedUrl) {
    return;
  }
  const link = document.createElement("a");
  link.href = entry.signedUrl;
  link.download = `${entry.name || "annotated-image"}-${entry.id}.png`;
  link.target = "_blank";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
function _temp4() {
  toast({
    title: "Restore failed",
    description: "Failed to restore annotations. Please try again.",
    variant: "error"
  });
}
function _temp3(query) {
  const key = query.queryKey;
  if (!Array.isArray(key)) {
    return false;
  }
  if (key.length >= 3 && key[0] === "scribe" && key[1] === "notes" && key[2] === "attachments") {
    return true;
  }
  if (key.length >= 3 && key[0] === "scribe" && key[1] === "notes" && key[2] === "attachments-infinite") {
    return true;
  }
  return false;
}
function _temp2(data) {
  if (!data) {
    return data;
  }
  return [...data].sort(_temp);
}
function _temp(a, b) {
  const dateA = a.createdDate ? new Date(a.createdDate).getTime() : 0;
  const dateB = b.createdDate ? new Date(b.createdDate).getTime() : 0;
  return dateB - dateA;
}